// Modal wrapper for ClientFormFields — handles create + edit + archive.
import { useRef, useState } from "react";
import { X, Loader2, Save, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ClientFormFields from "./ClientFormFields";

export default function ClientFormDialog({ open, mode = "create", initial = null, onClose, onSaved }) {
    const [form, setForm] = useState(initial || {});
    const [busy, setBusy] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [pendingLogo, setPendingLogo] = useState(null);
    const modulesTouchedRef = useRef(false);

    if (!open) return null;

    async function save() {
        if (!form.name?.trim()) {
            toast.error("Client name is required");
            return;
        }
        // Strip empty-string optional fields so Pydantic EmailStr & friends don't 422
        let cleaned = Object.fromEntries(
            Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
        );
        // Editing an existing client: never blindly re-send Module configuration
        // unless the admin actually touched it this session — otherwise saving for
        // an unrelated reason (contact info, branding, risk...) on a client whose
        // modules were never explicitly configured silently collapses them all
        // down to a bare "dashboard only" set.
        if (mode === "edit" && !modulesTouchedRef.current) {
            const { portal_modules, portal_module_mode, portal_preset_id, ...rest } = cleaned;
            cleaned = rest;
        }
        setBusy(true);
        try {
            if (mode === "create") {
                let { data } = await api.post("/clients", cleaned);
                // A logo picked before the client existed is uploaded now that we have an id.
                if (pendingLogo) {
                    try {
                        const fd = new FormData();
                        fd.append("file", pendingLogo);
                        const logoRes = await api.post(`/clients/${data.id}/upload-logo`, fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                        });
                        data = logoRes.data;
                    } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Client created, but logo upload failed");
                    }
                }
                toast.success(`Created ${data.name}`);
                onSaved?.(data);
            } else {
                const { data } = await api.patch(`/clients/${initial.id}`, cleaned);
                toast.success(`Updated ${data.name}`);
                onSaved?.(data);
            }
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    async function archive() {
        setBusy(true);
        try {
            await api.delete(`/clients/${initial.id}`);
            toast.success(`${initial.name} archived`);
            onSaved?.(null);
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Archive failed");
        } finally {
            setBusy(false);
            setArchiveOpen(false);
        }
    }

    const heading = mode === "create" ? "New client" : `Edit ${initial?.name || "client"}`;
    const sub = mode === "create"
        ? "Create the organisation, configure the branded portal, choose enabled modules."
        : "Update profile, branding and module configuration.";

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in"
             style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="client-form-dialog"
                className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[760px] max-h-[92vh] flex flex-col border"
                style={{ borderColor: "#E5E0DA" }}>
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-4" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center"
                         style={{ background: "#FBEFE5", color: "#9F4212" }}>
                        {mode === "create" ? <PlusCircle className="w-5 h-5" /> : <Save className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[20px] font-bold text-[#1F1F1F]">{heading}</h2>
                        <p className="text-[12.5px] mt-0.5" style={{ color: "#6F6A64" }}>{sub}</p>
                    </div>
                    <button data-testid="client-form-close" onClick={onClose} aria-label="Close"
                            className="w-9 h-9 rounded-[8px] grid place-items-center hover:bg-[#F6F4F0]">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto">
                    <ClientFormFields initial={initial} onChange={(f, touched) => { setForm(f); if (touched) modulesTouchedRef.current = true; }} onPendingLogo={setPendingLogo} />
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    {mode === "edit" && (
                        <button data-testid="client-form-archive"
                            onClick={() => setArchiveOpen(true)}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12px] font-semibold text-[#B33A2E] hover:bg-[#FBE8E5]">
                            <Trash2 className="w-3.5 h-3.5" /> Archive
                        </button>
                    )}
                    <div className="flex-1" />
                    <button data-testid="client-form-cancel" onClick={onClose} disabled={busy}
                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border"
                        style={{ borderColor: "#E5E0DA", color: "#2D2D2D", background: "#fff" }}>
                        Cancel
                    </button>
                    <button data-testid="client-form-save" onClick={save} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-70"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {busy ? "Saving…" : mode === "create" ? "Create client" : "Save changes"}
                    </button>
                </div>

                {/* Archive confirmation */}
                {archiveOpen && (
                    <div className="absolute inset-0 grid place-items-center p-6 rounded-[14px]"
                         style={{ background: "rgba(255,255,255,.97)" }}>
                        <div className="max-w-[420px] text-center">
                            <div className="w-12 h-12 rounded-full grid place-items-center mx-auto mb-3"
                                 style={{ background: "#FBE8E5", color: "#B33A2E" }}>
                                <Trash2 className="w-5 h-5" />
                            </div>
                            <h3 className="font-display text-[18px] font-bold mb-2">Archive {initial?.name}?</h3>
                            <p className="text-[12.5px]" style={{ color: "#6F6A64" }}>
                                The client will be hidden from lists and dashboards but no records are deleted.
                                You can restore them later from the audit log.
                            </p>
                            <div className="mt-5 flex items-center gap-2 justify-center">
                                <button onClick={() => setArchiveOpen(false)}
                                    className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border"
                                    style={{ borderColor: "#E5E0DA", background: "#fff" }}>
                                    Keep active
                                </button>
                                <button data-testid="client-form-archive-confirm" onClick={archive}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                                    style={{ background: "#B33A2E" }}>
                                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    Archive client
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
